import { useCallback } from "react";
import { CardContent, CardFooter } from "@components/ui/card";
import {
  useAdsChatIsReady,
  useAdsChatIsStreaming,
  useAdsChatIsLoadingHistory,
} from "@components/ads/hooks";
import { Chat } from "@launch10/use-langgraph-stream";
import { RECOMMENDATION_PANELS,
  GenericSubmission, recommendationIconFor } from "@components/chat/RecommendationPanels";
import { recommendationsForSurface } from "@lib/recommendationsRegistry";
import { useWorkflow, selectSubstep } from "@context/WorkflowProvider";
import AdsChatMessages from "./ads-chat/AdsChatMessages";
import AdsChatInput from "./ads-chat/AdsChatInput";

export default function AdsChat() {
  const isLoadingHistory = useAdsChatIsLoadingHistory();
  const isStreaming = useAdsChatIsStreaming();
  const isReady = useAdsChatIsReady();
  const substep = useWorkflow(selectSubstep);

  // Mirror useAdsComposer's formatSubmit: every submission carries the live
  // stage so handleIntentNode reads it from the envelope instead of a stale
  // checkpoint value. Recommendation submissions bypass the composer's
  // formatSubmit, so they decorate here.
  const decorateSubmit = useCallback(
    (payload: Record<string, unknown>) =>
      substep ? { ...payload, stage: substep } : payload,
    [substep]
  );

  return (
    <Chat.Recommendations.Root
      renderers={RECOMMENDATION_PANELS}
      defaultSubmission={GenericSubmission}
      // Static registry, narrowed to the ads page the user is on (entries
      // with `stages` only show on those substeps).
      recommendations={recommendationsForSurface("ads", substep ?? undefined)}
      iconFor={recommendationIconFor}
      decorateSubmit={decorateSubmit}
      notePlaceholder="Add a note for Houston… (optional)"
    >
      <div
        className="bg-neutral-background rounded-b-2xl flex flex-col flex-1 min-h-0 justify-between"
        data-testid="ads-chat"
        data-loading-history={isLoadingHistory}
        data-streaming={isStreaming}
        data-ready={isReady}
      >
        {/* No horizontal padding here: each message insets itself, so wide
            artifacts (keyword tables) can run edge-to-edge of the panel. */}
        <CardContent
          className="flex-1 px-0 py-4 min-h-0 flex flex-col"
          data-testid="ads-chat-messages"
        >
          <AdsChatMessages />
        </CardContent>
        <CardFooter className="flex-col items-stretch gap-2 px-4 pb-4 pt-0 w-full shrink-0">
          <Chat.Recommendations.Chips label="Suggested next steps" />
          <Chat.Recommendations.Composer>
            <AdsChatInput />
          </Chat.Recommendations.Composer>
        </CardFooter>
      </div>
    </Chat.Recommendations.Root>
  );
}
